import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Delete, Add } from "@mui/icons-material";
import { propertyAPI } from "../../../services/api/property";
import { tenantAPI } from "../../../services/api/tenant";

const assetOverviewLinkStyle = {
  color: "#1976d2",
  textDecoration: "underline",
  cursor: "pointer",
};

const tenantDisplayName = (t) =>
  t.name ||
  [t.last_name, t.first_name].filter(Boolean).join(", ") ||
  "Unbekannt";

const tenantsToLinks = (list) =>
  (list || []).map((t) => ({ id: t.id, name: tenantDisplayName(t) }));

const OverviewTab = () => {
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  // States for API data
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);

  // New entry form state
  const [newEntry, setNewEntry] = useState({
    property: "",
    property_id: null,
    currentTenant: "",
    tenant_id: null,
    noticeDate: "",
    moveOutDate: "",
    daysUntilMoveOut: 0,
    rentType: "",
    status: "",
    renovationRequired: false,
    renovationFinished: false,
    newTenant: [], // store array of tenant IDs
    moveInDate: "",
    depositRefunded: false,
    notes: "",
    assignedTo: "",
  });

  const statusLabels = {
    vacant: "Leer",
    rented: "Vermietet",
    maintenance: "Instandhaltung",
    occupied_owner: "Eigennutzung",
    terminated_on: "Gekündigt zum:",
    in_progress: "In Bearbeitung",
    new_open: "Neu / Offen",
    renovation_in_progress: "Renovierung läuft",
    looking_for_tenant: "Nachmieter gesucht",
    completed: "Abgeschlossen",
    renovation_planned: "Renovierung geplant",
  };

  const getStatusLabel = (status) => statusLabels[status] || status;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propsRes, bgRes] = await Promise.all([
          propertyAPI.getProperties({ limit: 10000 }),
          tenantAPI.getTenants({ limit: 10000 }),
        ]);
        const fetchedProps = propsRes.data || [];
        const fetchedTenants = bgRes.data || [];
        setProperties(fetchedProps);
        setTenants(fetchedTenants);

        // Pre-fill rows with properties that have terminated_on or move_out_date
        const initialRows = fetchedProps
          .filter((p) => p.terminated_on || p.move_out_date)
          .map((p, index) => {
            const currentTenants = tenantsToLinks(p.tenants);
            const activeTenantStr = currentTenants.length
              ? currentTenants.map((t) => t.name).join(", ")
              : "Unbekannt";

            let daysUntilMoveOut = 0;
            if (p.move_out_date) {
              const moveOut = new Date(p.move_out_date);
              const today = new Date();
              const diffTime = moveOut - today;
              daysUntilMoveOut = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }

            return {
              id: p.id, // Use property ID as row ID for easy updates
              property_id: p.id,
              property: `${p.address} (${p.property_number})`,
              currentTenant: activeTenantStr,
              currentTenants,
              noticeDate: p.terminated_on || "",
              moveOutDate: p.move_out_date || "",
              daysUntilMoveOut,
              rentType: p.rental_type || "",
              status: p.status || "",
              newTenant: p.new_tenants?.length
                ? p.new_tenants.map((t) => t.id)
                : [], // array of ids
              moveInDate: p.move_in_date || "",
              depositRefunded: false,
              notes: p.description || "",
              assignedTo: "",
              lastUpdated: p.updated_at
                ? p.updated_at.split("T")[0]
                : new Date().toISOString().split("T")[0],
            };
          });

        setRows(initialRows);
      } catch (err) {
        console.error("Error fetching properties/tenants", err);
      }
    };
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setNewEntry({
      property: "",
      property_id: null,
      currentTenant: "",
      tenant_id: null,
      noticeDate: "",
      moveOutDate: "",
      daysUntilMoveOut: 0,
      rentType: "",
      status: "",
      renovationRequired: false,
      renovationFinished: false,
      newTenant: [],
      moveInDate: "",
      depositRefunded: false,
      notes: "",
      assignedTo: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handlePropertySelect = (newValue) => {
    if (!newValue) {
      setNewEntry((prev) => ({
        ...prev,
        property: "",
        property_id: null,
      }));
      return;
    }

    const propName =
      newValue.address && newValue.property_number
        ? `${newValue.address} (${newValue.property_number})`
        : newValue.name || newValue.address || String(newValue.id || "");

    const updates = {
      property: propName,
      property_id: newValue.id,
    };

    if (!newEntry.currentTenant) {
      const firstTenantId =
        newValue.tenant_ids?.[0] || newValue.tenants?.[0]?.id;

      let selectedTenant = null;
      if (firstTenantId) {
        selectedTenant = tenants.find(
          (t) =>
            t.id === firstTenantId &&
            t.status !== "inactive" &&
            t.status !== "former" &&
            t.is_active !== false,
        );
      }

      if (!selectedTenant) {
        selectedTenant = tenants.find(
          (t) =>
            (t.property_ids?.includes(newValue.id) ||
              t.properties?.some((p) => p.id === newValue.id) ||
              t.property_id === newValue.id ||
              t.property === propName) &&
            t.status !== "inactive" &&
            t.status !== "former" &&
            t.is_active !== false,
        );
      }

      if (selectedTenant) {
        updates.currentTenant =
          [selectedTenant.last_name, selectedTenant.first_name]
            .filter(Boolean)
            .join(", ") || selectedTenant.name;
        updates.tenant_id = selectedTenant.id;
      }
    }

    setNewEntry((prev) => ({ ...prev, ...updates }));
  };

  const handleCurrentTenantSelect = (newValue) => {
    if (!newValue) {
      setNewEntry((prev) => ({
        ...prev,
        currentTenant: "",
        tenant_id: null,
      }));
      return;
    }

    const tenantName =
      [newValue.last_name, newValue.first_name].filter(Boolean).join(", ") ||
      newValue.name;

    const updates = {
      currentTenant: tenantName,
      tenant_id: newValue.id,
    };

    if (!newEntry.property) {
      const firstPropId =
        newValue.property_ids?.[0] ||
        newValue.properties?.[0]?.id ||
        newValue.property_id;

      let relatedProp = null;
      if (firstPropId) {
        relatedProp = properties.find((p) => p.id === firstPropId);
      }

      if (!relatedProp) {
        relatedProp = properties.find(
          (p) =>
            p.tenant_ids?.includes(newValue.id) ||
            p.tenants?.some((t) => t.id === newValue.id),
        );
      }

      if (relatedProp) {
        updates.property =
          relatedProp.address && relatedProp.property_number
            ? `${relatedProp.address} (${relatedProp.property_number})`
            : relatedProp.name || relatedProp.address || String(relatedProp.id);
        updates.property_id = relatedProp.id;
      }
    }

    setNewEntry((prev) => ({ ...prev, ...updates }));
  };

  const handleNewEntryChange = (field, value) => {
    setNewEntry({ ...newEntry, [field]: value });
  };

  const handleSaveEntry = async () => {
    if (!newEntry.property_id) {
      alert("Bitte wählen Sie eine Immobilie aus.");
      return;
    }
    if (!newEntry.currentTenant) {
      alert("Bitte wählen Sie einen Mieter aus.");
      return;
    }
    if (!newEntry.noticeDate) {
      alert("Bitte geben Sie ein Kündigungsdatum an.");
      return;
    }

    const payload = {
      terminated_on: newEntry.noticeDate || null,
      move_out_date: newEntry.moveOutDate || null,
      move_in_date: newEntry.moveInDate || null,
      status: newEntry.status || null,
      new_tenant_ids: Array.isArray(newEntry.newTenant)
        ? newEntry.newTenant
        : newEntry.newTenant
          ? [newEntry.newTenant]
          : [],
      description: newEntry.notes || "",
    };

    try {
      const res = await propertyAPI.updateProperty(
        newEntry.property_id,
        payload,
      );
      const updatedProp = res.data;

      // Update local properties list with returned property
      setProperties((prev) =>
        prev.map((p) => (p.id === updatedProp.id ? updatedProp : p)),
      );

      // Build display values for the new row
      const currentTenants = tenantsToLinks(updatedProp.tenants);
      const activeTenantStr = currentTenants.length
        ? currentTenants.map((t) => t.name).join(", ")
        : newEntry.currentTenant || "Unbekannt";

      const moveOut = updatedProp.move_out_date || newEntry.moveOutDate || null;
      let daysUntilMoveOut = 0;
      if (moveOut) {
        const diff = new Date(moveOut) - new Date();
        daysUntilMoveOut = Math.ceil(diff / (1000 * 60 * 60 * 24));
      }

      const newRow = {
        id: updatedProp.id,
        property_id: updatedProp.id,
        property: updatedProp.address
          ? `${updatedProp.address} (${updatedProp.property_number})`
          : String(updatedProp.id),
        currentTenant: activeTenantStr,
        currentTenants,
        noticeDate: updatedProp.terminated_on || newEntry.noticeDate || "",
        moveOutDate: updatedProp.move_out_date || newEntry.moveOutDate || "",
        daysUntilMoveOut,
        rentType: updatedProp.rental_type || newEntry.rentType || "",
        status: updatedProp.status || newEntry.status || "",
        newTenant: payload.new_tenant_ids || [],
        moveInDate: updatedProp.move_in_date || newEntry.moveInDate || "",
        depositRefunded: false,
        notes: updatedProp.description || newEntry.notes || "",
        assignedTo: "",
        lastUpdated: updatedProp.updated_at
          ? updatedProp.updated_at.split("T")[0]
          : new Date().toISOString().split("T")[0],
      };

      setRows((prev) => [...prev, newRow]);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error updating property during add:", error);
      alert("Fehler beim Anlegen des Eintrags. Bitte erneut versuchen.");
    }
  };

  const handleChange = (id, field, value) => {
    setRows(
      rows.map((row) => {
        if (row.id === id) {
          const updatedRow = {
            ...row,
            [field]: value,
            lastUpdated: new Date().toISOString().split("T")[0],
          };

          if (field === "moveOutDate") {
            if (value) {
              const moveOut = new Date(value);
              const today = new Date();
              const diffTime = moveOut - today;
              updatedRow.daysUntilMoveOut = Math.ceil(
                diffTime / (1000 * 60 * 60 * 24),
              );
            } else {
              updatedRow.daysUntilMoveOut = 0;
            }
          }

          return updatedRow;
        }
        return row;
      }),
    );
  };

  const handleSaveRow = async (row) => {
    try {
      const propToUpdate = properties.find((p) => p.id === row.property_id);
      if (!propToUpdate) return;

      const payload = {
        terminated_on: row.noticeDate || null,
        move_out_date: row.moveOutDate || null,
        move_in_date: row.moveInDate || null,
        rental_type: row.rentType || null,
        status: row.status || null,
        new_tenant_ids: row.newTenant,
        description: row.notes || "",
      };

      await propertyAPI.updateProperty(row.property_id, payload);
      alert("Erfolgreich gespeichert!");
    } catch (error) {
      console.error("Error saving property changes:", error);
      alert("Fehler beim Speichern!");
    }
  };
  const handleCompleteTenantChange = async (row) => {
    try {
      if (
        !window.confirm(
          "Möchten Sie den Mieterwechsel wirklich abschließen? Aktuelle Mieter werden auf inaktiv gesetzt, Nachmieter werden als aktiv übernommen und das Objekt auf 'Vermietet' zurückgesetzt.",
        )
      ) {
        return;
      }

      await propertyAPI.completeTenantChange(row.property_id);

      // Remove the row since it no longer has an active termination or move-out date
      setRows(rows.filter((r) => r.id !== row.id));
      alert("Mieterwechsel erfolgreich abgeschlossen!");
    } catch (error) {
      console.error("Fehler beim Mieterwechsel", error);
      alert("Fehler beim Abschluss des Mieterwechsels!");
    }
  };
  const inactiveTenants = tenants.filter(
    (t) =>
      t.status === "inactive" || t.status === "former" || t.is_active === false,
  );

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContents: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Mieterwechsel Übersicht
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          Neuer Eintrag
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Immobilie / Wohnung</TableCell>
              <TableCell>Aktueller Mieter</TableCell>
              <TableCell>Kündigungsdatum</TableCell>
              <TableCell>Auszugsdatum</TableCell>
              <TableCell>
                Tage bis
                <br />
                Auszug
              </TableCell>
              <TableCell>Vermietungstyp</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Neuer Mieter</TableCell>
              <TableCell>Einzugsdatum</TableCell>
              <TableCell>Maßnahmen / Notizen</TableCell>
              <TableCell>Zuletzt aktualisiert</TableCell>
              <TableCell align="center">Speichern</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice()
              .sort((a, b) => {
                const av =
                  typeof a.daysUntilMoveOut === "number"
                    ? a.daysUntilMoveOut
                    : Number.POSITIVE_INFINITY;
                const bv =
                  typeof b.daysUntilMoveOut === "number"
                    ? b.daysUntilMoveOut
                    : Number.POSITIVE_INFINITY;
                return av - bv;
              })
              .map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    <a
                      href={`/asset-overview?tab=properties&search=${row.property_id}`}
                      style={assetOverviewLinkStyle}
                    >
                      {row.property}
                    </a>
                  </TableCell>
                  <TableCell>
                    {row.currentTenants?.length ? (
                      row.currentTenants.map((t, i) => (
                        <React.Fragment key={t.id}>
                          {i > 0 && ", "}
                          <a
                            href={`/asset-overview?tab=tenants&search=id=${t.id}`}
                            style={assetOverviewLinkStyle}
                          >
                            {t.name}
                          </a>
                        </React.Fragment>
                      ))
                    ) : (
                      <Typography variant="body2" component="span">
                        {row.currentTenant}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      variant="standard"
                      value={row.noticeDate}
                      onChange={(e) =>
                        handleChange(row.id, "noticeDate", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      variant="standard"
                      value={row.moveOutDate}
                      onChange={(e) =>
                        handleChange(row.id, "moveOutDate", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.daysUntilMoveOut}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={row.rentType}
                      onChange={(e) =>
                        handleChange(row.id, "rentType", e.target.value)
                      }
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="">
                        <em>Leer</em>
                      </MenuItem>
                      <MenuItem value="direct_rental">
                        Direktvermietung
                      </MenuItem>
                      <MenuItem value="tenant_search">
                        Nachmieter durch Mieter
                      </MenuItem>
                      <MenuItem value="renovation_new_rental">
                        Renovierung + Neuvermietung
                      </MenuItem>
                      <MenuItem value="planned_vacancy">
                        Leerstand geplant
                      </MenuItem>
                      <MenuItem value="personal_use">Eigenbedarf</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={row.status}
                      onChange={(e) =>
                        handleChange(row.id, "status", e.target.value)
                      }
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value="">
                        <em>Leer</em>
                      </MenuItem>
                      {Object.entries(statusLabels).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      options={inactiveTenants}
                      filterOptions={(options, { inputValue }) => {
                        const input = (inputValue || "").trim().toLowerCase();
                        if (!input) return options;
                        return options.filter((t) => {
                          const combined = `${t.name || ""} ${
                            t.first_name || ""
                          } ${t.last_name || ""} ${t.tenant_number || ""} ${
                            t.email || ""
                          }`.toLowerCase();
                          return combined.includes(input);
                        });
                      }}
                      getOptionLabel={(option) =>
                        option.name ||
                        `${option.first_name || ""} ${
                          option.last_name || ""
                        }`.trim()
                      }
                      value={inactiveTenants.filter((t) =>
                        (row.newTenant || []).includes(t.id),
                      )}
                      onChange={(e, newValue) => {
                        handleChange(
                          row.id,
                          "newTenant",
                          newValue.map((t) => t.id),
                        );
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, onDelete, ...tagProps } = getTagProps({
                            index,
                          });
                          const label =
                            option.name ||
                            `${option.first_name || ""} ${
                              option.last_name || ""
                            }`.trim();
                          return (
                            <Chip
                              key={key}
                              size="small"
                              {...tagProps}
                              onDelete={onDelete}
                              label={
                                <a
                                  href={`/asset-overview?tab=tenants&search=id=${option.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={assetOverviewLinkStyle}
                                >
                                  {label}
                                </a>
                              }
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          size="small"
                          sx={{ minWidth: 150 }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      variant="standard"
                      value={row.moveInDate}
                      onChange={(e) =>
                        handleChange(row.id, "moveInDate", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={row.notes}
                      onChange={(e) =>
                        handleChange(row.id, "notes", e.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>{row.lastUpdated}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveRow(row)}
                      >
                        Speichern
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleCompleteTenantChange(row)}
                        title="Zieht aktuelle Mieter aus, Nachmieter ein und schließt den Mieterwechsel ab."
                      >
                        Abschließen
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  Keine Einträge vorhanden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neuer Eintrag hinzufügen</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
        >
          <Autocomplete
            options={properties}
            getOptionLabel={(option) =>
              option && option.address && option.property_number
                ? `${option.address} (${option.property_number}), ${option.city}`
                : option?.name || option?.address || String(option?.id || "")
            }
            filterOptions={(options, { inputValue }) => {
              const input = (inputValue || "").trim().toLowerCase();
              if (!input) return options;
              return options.filter((opt) => {
                const combined = `${opt.address || ""} ${
                  opt.property_number || ""
                } ${opt.city || ""}`.toLowerCase();
                return combined.includes(input);
              });
            }}
            value={
              properties.find((p) => p.id === newEntry.property_id) || null
            }
            onChange={(event, newValue) => handlePropertySelect(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Immobilie / Wohnung auswählen"
                placeholder="Adresse oder Nummer suchen..."
                variant="outlined"
                fullWidth
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <Autocomplete
            options={tenants.filter(
              (t) =>
                t.status !== "inactive" &&
                t.status !== "former" &&
                t.is_active !== false,
            )}
            filterOptions={(options, { inputValue }) => {
              const input = (inputValue || "").trim().toLowerCase();
              if (!input) return options;
              return options.filter((t) => {
                const combined = `${t.name || ""} ${
                  t.tenant_number || ""
                }`.toLowerCase();
                return combined.includes(input);
              });
            }}
            getOptionLabel={(option) =>
              option
                ? [option.last_name, option.first_name]
                    .filter(Boolean)
                    .join(", ") || option.name
                : ""
            }
            value={tenants.find((t) => t.id === newEntry.tenant_id) || null}
            onChange={(event, newValue) => handleCurrentTenantSelect(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Aktueller Mieter"
                placeholder="Mieter suchen..."
                variant="outlined"
                fullWidth
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <TextField
            fullWidth
            type="date"
            label="Kündigungsdatum (gekündigt zum)"
            InputLabelProps={{ shrink: true }}
            value={newEntry.noticeDate}
            onChange={(e) => handleNewEntryChange("noticeDate", e.target.value)}
            variant="outlined"
            required
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <TextField
            fullWidth
            type="date"
            label="Auszugsdatum"
            InputLabelProps={{ shrink: true }}
            value={newEntry.moveOutDate}
            onChange={(e) =>
              handleNewEntryChange("moveOutDate", e.target.value)
            }
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          <Autocomplete
            multiple
            options={inactiveTenants}
            filterOptions={(options, { inputValue }) => {
              const input = (inputValue || "").trim().toLowerCase();
              if (!input) return options;
              return options.filter((t) => {
                const combined = `${t.name || ""} ${t.first_name || ""} ${
                  t.last_name || ""
                } ${t.tenant_number || ""} ${t.email || ""}`.toLowerCase();
                return combined.includes(input);
              });
            }}
            getOptionLabel={(option) =>
              option
                ? [option.last_name, option.first_name]
                    .filter(Boolean)
                    .join(", ") || option.name
                : ""
            }
            value={inactiveTenants.filter((t) =>
              (newEntry.newTenant || []).includes(t.id),
            )}
            onChange={(event, newValue) => {
              handleNewEntryChange(
                "newTenant",
                newValue.map((t) => t.id),
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Neuer Mieter (Inaktive Mieter)"
                placeholder="Name suchen..."
                variant="outlined"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSaveEntry} variant="contained" color="primary">
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OverviewTab;
